import { useState } from 'react'
import { Calendar, Mail, Check, Star, Plus, RefreshCw, Loader2, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DataRow, ToggleSwitch } from '@tendbot/ui'
import type { PluginConnectionDetails } from '../types'

interface PluginConnectionExpandedProps {
  connectionDetails: PluginConnectionDetails
  /** Additional connections for multi-account display */
  allConnections?: PluginConnectionDetails[]
  defaultConnectionId?: string | null
  onConnectGmail?: () => void
  onConnectCalendar?: () => void
  onConnectContacts?: () => void
  onDisconnect: (connectionId?: string) => void
  onSetDefault?: (connectionId: string) => void
  onAddAccount?: () => void
  onToggleCalendarSync?: (calendarId: string, syncEnabled: boolean) => void
  onToggleServiceSync?: (connectionId: string, services: { calendarSyncEnabled?: boolean; emailSyncEnabled?: boolean }) => void
  /** Trigger a bootstrap sync (run-through). Only shown if provided. */
  onRunThrough?: () => Promise<void>
  /** ISO timestamp of last run-through, used to show in-progress state */
  runThroughLastRunAt?: string | null
}

function ConnectionSection({
  connection,
  isDefault,
  locale,
  onConnectGmail,
  onConnectCalendar,
  onConnectContacts,
  onDisconnect,
  onSetDefault,
  onToggleCalendarSync,
  onToggleServiceSync,
  t,
  tc,
}: {
  connection: PluginConnectionDetails
  isDefault: boolean
  locale: string
  onConnectGmail?: () => void
  onConnectCalendar?: () => void
  onConnectContacts?: () => void
  onDisconnect: () => void
  onSetDefault?: () => void
  onToggleCalendarSync?: (calendarId: string, syncEnabled: boolean) => void
  onToggleServiceSync?: (connectionId: string, services: { calendarSyncEnabled?: boolean; emailSyncEnabled?: boolean }) => void
  t: (key: string) => string
  tc: (key: string) => string
}) {
  const connectedDate = new Date(connection.connectedAt).toLocaleDateString(locale, {
    year: 'numeric', month: 'long', day: 'numeric',
  })

  const calendarEnabled = connection.calendarSyncEnabled !== false
  const emailEnabled = connection.emailSyncEnabled !== false

  return (
    <div className="space-y-3 p-3 rounded-xl bg-midnight/40 border border-white/5">
      {/* Account header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <DataRow label={t('connection.account')} value={connection.displayName} />
          {isDefault && (
            <span className="flex items-center gap-1 text-[10px] text-gold/70">
              <Star size={10} fill="currentColor" />
              {t('connection.defaultBadge')}
            </span>
          )}
        </div>
        <DataRow label={t('connection.connected')} value={connectedDate} />
      </div>

      {/* Services — Google-specific */}
      {connection.provider === 'google' && (
        <div className="space-y-2">
          <span className="text-xs text-pearl/50 block">{t('connection.services')}</span>

          {/* Calendar service */}
          <div className="bg-charcoal/50 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 text-xs px-2.5 py-2">
              <Calendar size={12} className={connection.calendarConnected && calendarEnabled ? 'text-gold' : 'text-pearl/55'} />
              <span className={`flex-1 font-medium ${connection.calendarConnected && calendarEnabled ? 'text-pearl' : 'text-pearl/60'}`}>
                {t('connection.calendarAccess')}
              </span>
              {connection.calendarConnected && onToggleServiceSync ? (
                <ToggleSwitch
                  size="sm"
                  variant="bordered"
                  checked={calendarEnabled}
                  onChange={() => onToggleServiceSync(connection.connectionId, { calendarSyncEnabled: !calendarEnabled })}
                />
              ) : !connection.calendarConnected && onConnectCalendar ? (
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onConnectCalendar()
                  }}
                  className="text-xs text-gold hover:text-gold-light transition-colors"
                >
                  {tc('actions.connect')}
                </button>
              ) : null}
            </div>
            {connection.calendarConnected && calendarEnabled && (connection.calendars?.length ?? 0) > 0 && (
              <div className="border-t border-white/5 px-2.5 py-1.5 space-y-1">
                {connection.calendars!.map((cal) => (
                  <div key={cal.id} className="flex items-center gap-2 text-xs py-0.5 pl-4">
                    <Calendar size={10} className={cal.syncEnabled ? (cal.isPrimary ? 'text-gold/60' : 'text-pearl/55') : 'text-pearl/45'} />
                    <span className={`flex-1 ${cal.syncEnabled ? 'text-pearl/70' : 'text-pearl/55'}`}>{cal.name}</span>
                    {cal.isPrimary && (
                      <span className="text-[10px] text-gold/50">{t('connection.primary')}</span>
                    )}
                    {onToggleCalendarSync && (
                      <ToggleSwitch
                        size="sm"
                        variant="bordered"
                        checked={cal.syncEnabled}
                        onChange={() => onToggleCalendarSync(cal.id, !cal.syncEnabled)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gmail service */}
          <div className="bg-charcoal/50 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 text-xs px-2.5 py-2">
              <Mail size={12} className={connection.emailConnected && emailEnabled ? 'text-gold' : 'text-pearl/55'} />
              <span className={`flex-1 font-medium ${connection.emailConnected && emailEnabled ? 'text-pearl' : 'text-pearl/60'}`}>
                {t('connection.gmailAccess')}
              </span>
              {connection.emailConnected && onToggleServiceSync ? (
                <ToggleSwitch
                  size="sm"
                  variant="bordered"
                  checked={emailEnabled}
                  onChange={() => onToggleServiceSync(connection.connectionId, { emailSyncEnabled: !emailEnabled })}
                />
              ) : !connection.emailConnected && onConnectGmail ? (
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onConnectGmail()
                  }}
                  className="text-xs text-gold hover:text-gold-light transition-colors"
                >
                  {tc('actions.connect')}
                </button>
              ) : null}
            </div>
            {connection.emailConnected && emailEnabled && (
              <div className="border-t border-white/5 px-2.5 py-1.5">
                <div className="flex items-center gap-2 text-xs pl-4">
                  <Mail size={10} className="text-pearl/55" />
                  <span className="text-pearl/70">{connection.displayName}</span>
                </div>
              </div>
            )}
          </div>

          {/* Contacts service */}
          <div className="bg-charcoal/50 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 text-xs px-2.5 py-2">
              <Users size={12} className={connection.contactsConnected ? 'text-gold' : 'text-pearl/55'} />
              <span className={`flex-1 font-medium ${connection.contactsConnected ? 'text-pearl' : 'text-pearl/60'}`}>
                {t('connection.contactsAccess')}
              </span>
              {connection.contactsConnected ? (
                <span className="text-[10px] text-emerald/70">{tc('actions.connected')}</span>
              ) : onConnectContacts ? (
                <button
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onConnectContacts()
                  }}
                  className="text-xs text-gold hover:text-gold-light transition-colors"
                >
                  {tc('actions.connect')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Services — Microsoft-specific */}
      {connection.provider === 'microsoft' && (
        <div className="space-y-2">
          <span className="text-xs text-pearl/50 block">{t('connection.services')}</span>

          {/* Outlook Calendar service */}
          <div className="bg-charcoal/50 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 text-xs px-2.5 py-2">
              <Calendar size={12} className={calendarEnabled ? 'text-gold' : 'text-pearl/55'} />
              <span className={`flex-1 font-medium ${calendarEnabled ? 'text-pearl' : 'text-pearl/60'}`}>
                {t('connection.outlookCalendarAccess')}
              </span>
              {onToggleServiceSync && (
                <ToggleSwitch
                  size="sm"
                  variant="bordered"
                  checked={calendarEnabled}
                  onChange={() => onToggleServiceSync(connection.connectionId, { calendarSyncEnabled: !calendarEnabled })}
                />
              )}
            </div>
            {calendarEnabled && (connection.calendars?.length ?? 0) > 0 && (
              <div className="border-t border-white/5 px-2.5 py-1.5 space-y-1">
                {connection.calendars!.map((cal) => (
                  <div key={cal.id} className="flex items-center gap-2 text-xs py-0.5 pl-4">
                    <Calendar size={10} className={cal.syncEnabled ? (cal.isPrimary ? 'text-gold/60' : 'text-pearl/55') : 'text-pearl/45'} />
                    <span className={`flex-1 ${cal.syncEnabled ? 'text-pearl/70' : 'text-pearl/55'}`}>{cal.name}</span>
                    {cal.isPrimary && (
                      <span className="text-[10px] text-gold/50">{t('connection.primary')}</span>
                    )}
                    {onToggleCalendarSync && (
                      <ToggleSwitch
                        size="sm"
                        variant="bordered"
                        checked={cal.syncEnabled}
                        onChange={() => onToggleCalendarSync(cal.id, !cal.syncEnabled)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outlook Email service */}
          <div className="bg-charcoal/50 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 text-xs px-2.5 py-2">
              <Mail size={12} className={emailEnabled ? 'text-gold' : 'text-pearl/55'} />
              <span className={`flex-1 font-medium ${emailEnabled ? 'text-pearl' : 'text-pearl/60'}`}>
                {t('connection.outlookAccess')}
              </span>
              {onToggleServiceSync && (
                <ToggleSwitch
                  size="sm"
                  variant="bordered"
                  checked={emailEnabled}
                  onChange={() => onToggleServiceSync(connection.connectionId, { emailSyncEnabled: !emailEnabled })}
                />
              )}
            </div>
            {emailEnabled && (
              <div className="border-t border-white/5 px-2.5 py-1.5">
                <div className="flex items-center gap-2 text-xs pl-4">
                  <Mail size={10} className="text-pearl/55" />
                  <span className="text-pearl/70">{connection.displayName}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Non-Google/Microsoft: plain calendar list fallback */}
      {connection.provider !== 'google' && connection.provider !== 'microsoft' && (connection.calendars?.length ?? 0) > 0 && (
        <div>
          <span className="text-xs text-pearl/50 block mb-1.5">{t('connection.calendarAccess')}</span>
          <div className="bg-charcoal/50 rounded-lg p-2.5 space-y-1.5">
            {connection.calendars!.map((cal) => (
              <div key={cal.id} className="flex items-center gap-2 text-xs">
                <Calendar size={12} className={cal.isPrimary ? 'text-gold' : 'text-pearl/60'} />
                <span className="text-pearl flex-1">{cal.name}</span>
                {onToggleCalendarSync && (
                  <ToggleSwitch
                    size="sm"
                    variant="bordered"
                    checked={cal.syncEnabled}
                    onChange={() => onToggleCalendarSync(cal.id, !cal.syncEnabled)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions row */}
      <div className="flex gap-2 pt-1">
        {!isDefault && onSetDefault && (
          <button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onSetDefault()
            }}
            className="flex-1 py-1.5 rounded-lg text-gold text-xs font-medium hover:bg-gold/10 transition-colors"
          >
            {t('connection.setDefault')}
          </button>
        )}
        <button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onDisconnect()
          }}
          className="flex-1 py-1.5 rounded-lg bg-rose/10 text-rose text-xs font-medium hover:bg-rose/20 transition-colors"
        >
          {t('connection.disconnect')}
        </button>
      </div>
    </div>
  )
}

export default function PluginConnectionExpanded({
  connectionDetails,
  allConnections,
  defaultConnectionId,
  onConnectGmail,
  onConnectCalendar,
  onConnectContacts,
  onDisconnect,
  onSetDefault,
  onAddAccount,
  onToggleCalendarSync,
  onToggleServiceSync,
  onRunThrough,
  runThroughLastRunAt,
}: PluginConnectionExpandedProps) {
  const { t, i18n } = useTranslation('plugins')
  const { t: tc } = useTranslation('common')

  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'idle' | 'success' | 'error'>('idle')

  // Check if a run-through is currently in progress (started < 5 min ago)
  // eslint-disable-next-line react-hooks/purity -- Date.now() is intentionally re-evaluated each render
  const isRunThroughInProgress = syncing || (!!runThroughLastRunAt && Date.now() - new Date(runThroughLastRunAt).getTime() < 5 * 60 * 1000)

  const connections = allConnections && allConnections.length > 0 ? allConnections : [connectionDetails]
  const hasMultiple = connections.length > 1

  async function handleSync(e: React.MouseEvent) {
    e.stopPropagation()
    if (!onRunThrough || isRunThroughInProgress) return
    setSyncing(true)
    setSyncStatus('idle')
    try {
      await onRunThrough()
      // The POST returns immediately (fire-and-forget dispatch).
      // Keep showing "Syncing..." while the plugin processes, then show success.
      await new Promise(resolve => setTimeout(resolve, 8000))
      setSyncing(false)
      setSyncStatus('success')
      setTimeout(() => setSyncStatus('idle'), 3000)
    } catch {
      setSyncing(false)
      setSyncStatus('error')
      setTimeout(() => setSyncStatus('idle'), 3000)
    }
  }

  return (
    <div className="space-y-3">
      {/* Account count badge */}
      {hasMultiple && (
        <span className="text-xs text-pearl/50">
          {t('connection.accountCount', { count: connections.length })}
        </span>
      )}

      {connections.map((conn, idx) => {
        const isDefault = defaultConnectionId
          ? conn.connectionId === defaultConnectionId
          : idx === 0
        return (
          <ConnectionSection
            key={conn.connectionId}
            connection={conn}
            isDefault={isDefault}
            locale={i18n.language}
            onConnectGmail={onConnectGmail}
            onConnectCalendar={onConnectCalendar}
            onConnectContacts={onConnectContacts}
            onDisconnect={() => onDisconnect(conn.connectionId)}
            onSetDefault={hasMultiple && onSetDefault ? () => onSetDefault(conn.connectionId) : undefined}
            onToggleCalendarSync={onToggleCalendarSync}
            onToggleServiceSync={onToggleServiceSync}
            t={t}
            tc={tc}
          />
        )
      })}

      {/* Add another account */}
      {onAddAccount && (
        <button
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onAddAccount()
          }}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-gold/30 text-gold text-xs font-medium hover:bg-gold/5 transition-colors"
        >
          <Plus size={14} />
          {connectionDetails.provider === 'microsoft'
            ? t('connection.addAnotherMicrosoft')
            : t('connection.addAnother')}
        </button>
      )}

      {/* Sync contacts button */}
      {onRunThrough && (
        <button
          onClick={handleSync}
          disabled={isRunThroughInProgress}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gold/20 text-gold text-xs font-medium hover:bg-gold/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRunThroughInProgress ? (
            <Loader2 size={14} className="animate-spin" />
          ) : syncStatus === 'success' ? (
            <Check size={14} className="text-emerald" />
          ) : (
            <RefreshCw size={14} />
          )}
          {isRunThroughInProgress
            ? t('connection.syncing')
            : syncStatus === 'success'
              ? t('connection.syncSuccess')
              : syncStatus === 'error'
                ? t('connection.syncError')
                : t('connection.syncContacts')}
        </button>
      )}
    </div>
  )
}
